import type { MessageType } from '@arco-design/web-react/es/Message';
import type { MessageInterface, MessageMethod, MessageProps } from './const';
import { Message as ArcoMessage } from '@arco-design/web-react';
import { cn } from '@utils';
import { IconCheck, IconClose, IconInfo, IconWarningSimple } from '@wegic/icon';
import './index.less';

const DEFAULT_CLASSNAME = 'wg-message';
const DEFAULT_SUCCESS_CLASSNAME = 'wg-message--success';
const DEFAULT_NODE_MESSAGE_CLASSNAME = 'wg-message--node';
const DEFAULT_ERROR_CLASSNAME = 'wg-message--error';

function normalizeConfig(config: MessageProps | string): MessageProps {
  return typeof config === 'string' ? { content: config } : config;
}

function buildConfig(
  config: MessageProps,
  icon: React.ReactNode | undefined,
  ...classNames: (string | undefined)[]
): MessageProps {
  const isNodeContent = typeof config.content !== 'string';
  const allClassNames = classNames.filter(Boolean);

  if (isNodeContent) {
    allClassNames.push(DEFAULT_NODE_MESSAGE_CLASSNAME);
  }

  return {
    ...config,
    ...(icon && { icon }),
    className: cn(...allClassNames, config.className),
  };
}

function MessageNormal(config: MessageProps | string): MessageType {
  const normalizedConfig = normalizeConfig(config);
  const finalConfig = buildConfig(normalizedConfig, undefined, DEFAULT_CLASSNAME);
  return ArcoMessage.normal(finalConfig);
}

function MessageSuccess(config: MessageProps | string): MessageType {
  const normalizedConfig = normalizeConfig(config);
  const icon = <IconCheck fontSize={16} style={{ verticalAlign: '-2px' }} />;
  const finalConfig = buildConfig(normalizedConfig, icon, DEFAULT_SUCCESS_CLASSNAME);
  return ArcoMessage.success(finalConfig);
}

function MessageError(config: MessageProps | string): MessageType {
  const normalizedConfig = normalizeConfig(config);
  const icon = <IconClose fontSize={16} style={{ verticalAlign: '-2px' }} />;
  const finalConfig = buildConfig(normalizedConfig, icon, DEFAULT_ERROR_CLASSNAME);
  return ArcoMessage.error(finalConfig);
}

function MessageWarning(config: MessageProps | string): MessageType {
  const normalizedConfig = normalizeConfig(config);
  const icon = <IconWarningSimple fontSize={16} style={{ verticalAlign: '-2px' }} />;
  const finalConfig = buildConfig(normalizedConfig, icon, DEFAULT_CLASSNAME);
  return ArcoMessage.warning(finalConfig);
}

function MessageInfo(config: MessageProps | string): MessageType {
  const normalizedConfig = normalizeConfig(config);
  const icon = <IconInfo fontSize={16} style={{ verticalAlign: '-2px' }} />;
  const finalConfig = buildConfig(normalizedConfig, icon, DEFAULT_CLASSNAME);
  return ArcoMessage.info(finalConfig);
}

const Message: MessageInterface = {
  ...ArcoMessage,
  error: MessageError,
  success: MessageSuccess,
  normal: MessageNormal,
  warning: MessageWarning,
  info: MessageInfo,
};

export default Message;

export type { MessageInterface, MessageMethod, MessageProps };
